import type { Column } from "@shared/types/data-table"
import type { BillingSchedule } from "@features/billing-schedules/types/billing-schedule.types"
import type { ColumnManagementConfig } from "@shared/lib/tables/utils/column-management"
import {
  createTextColumn,
  createStatusColumn,
  createDateColumn,
  createCustomColumn
} from "./column-factories"

export const BILLING_SCHEDULE_COLUMNS: Record<string, Column<BillingSchedule>> = {
  customerName: createTextColumn<BillingSchedule>("customerName", "Customer Name", 200, "font-medium text-sm"),
  customerEmail: createTextColumn<BillingSchedule>("customerEmail", "Customer Email", 220, "text-sm text-muted-foreground"),
  status: createStatusColumn<BillingSchedule>("billing-schedule"),
  products: createCustomColumn<BillingSchedule>(
    "products",
    "Products",
    200,
    (schedule: BillingSchedule) => (
      <span className="text-sm">{schedule.products.map(p => p.name).join(', ')}</span>
    )
  ),
  startDate: createDateColumn<BillingSchedule>("startDate", "Start Date", 120, false),
  endDate: createDateColumn<BillingSchedule>("endDate", "End Date", 120, false),
}

const DEFAULT_COLUMNS = ['customerName', 'customerEmail', 'status', 'products', 'startDate', 'endDate'] as const

const BILLING_SCHEDULE_STATUS_CONFIG: Record<string, readonly string[]> = {
  all: DEFAULT_COLUMNS,
  active: DEFAULT_COLUMNS,
  draft: DEFAULT_COLUMNS,
  completed: DEFAULT_COLUMNS,
  archived: DEFAULT_COLUMNS,
}

export const getBillingScheduleColumns = (status: string = 'all'): Column<BillingSchedule>[] => {
  const keys = BILLING_SCHEDULE_STATUS_CONFIG[status] || BILLING_SCHEDULE_STATUS_CONFIG.all
  return keys.map(key => BILLING_SCHEDULE_COLUMNS[key]).filter(Boolean)
}

export const getBillingScheduleColumnConfig = (status: string = 'all'): ColumnManagementConfig[] => {
  const visibleKeys = new Set(BILLING_SCHEDULE_STATUS_CONFIG[status] || BILLING_SCHEDULE_STATUS_CONFIG.all)
  return Object.entries(BILLING_SCHEDULE_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key),
    required: ['customerName', 'status'].includes(key),
    order: index
  }))
}